import * as React from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

// Hook to detect mobile/touch devices
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      setIsMobile(hasTouch || isSmallScreen || isIOS);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Pick a date",
  className,
  "data-testid": dataTestId,
  ...props 
}) {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  
  // Convert string date to Date object if needed
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    try {
      return parseISO(value);
    } catch {
      return undefined;
    }
  }, [value]);

  const handleSelect = (date) => {
    if (date) {
      const formatted = format(date, "yyyy-MM-dd");
      onChange(formatted);
    }
    setOpen(false);
  };

  // Trigger button - same for both mobile and desktop
  const TriggerButton = (
    <Button
      type="button"
      variant="outline"
      onClick={() => setOpen(true)}
      className={cn(
        "w-full h-12 justify-start text-left font-normal touch-manipulation",
        !dateValue && "text-muted-foreground",
        className
      )}
      data-testid={dataTestId || "date-picker-trigger"}
      {...props}
    >
      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
      <span>{dateValue ? format(dateValue, "MMM d, yyyy") : placeholder}</span>
    </Button>
  );

  // Mobile: Full-screen modal with safe areas
  if (isMobile) {
    return (
      <>
        {TriggerButton}
        {open && (
          <div 
            className="fixed inset-0 z-[100] bg-background flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-heading font-semibold text-lg">Select Date</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="h-10 w-10 rounded-full"
              >
                <X size={20} />
              </Button>
            </div>
            
            {/* Calendar container - centered with proper spacing */}
            <div className="flex-1 flex items-start justify-center overflow-y-auto pt-6 pb-8 px-4">
              <Calendar
                mode="single"
                selected={dateValue}
                onSelect={handleSelect}
                defaultMonth={dateValue}
                className="rounded-xl border bg-card shadow-lg"
                classNames={{
                  months: "flex flex-col p-4",
                  month: "space-y-6",
                  caption: "flex justify-center pt-1 relative items-center h-12",
                  caption_label: "text-lg font-bold",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-12 w-12 bg-muted hover:bg-accent rounded-full p-0 flex items-center justify-center touch-manipulation",
                  nav_button_previous: "absolute left-2",
                  nav_button_next: "absolute right-2",
                  table: "w-full border-collapse",
                  head_row: "flex justify-around mb-2",
                  head_cell: "text-muted-foreground rounded-md w-12 font-semibold text-sm",
                  row: "flex w-full mt-1 justify-around",
                  cell: "relative p-0.5 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-full",
                  day: "h-12 w-12 p-0 font-medium rounded-full hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground aria-selected:opacity-100 touch-manipulation transition-colors",
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                  day_today: "bg-accent text-accent-foreground font-bold ring-2 ring-primary/30",
                  day_outside: "text-muted-foreground opacity-40",
                  day_disabled: "text-muted-foreground opacity-40",
                  day_hidden: "invisible",
                }}
              />
            </div>
            
            {/* Current selection display */}
            <div className="p-4 border-t border-border bg-muted/50">
              <p className="text-center text-sm text-muted-foreground">
                {dateValue ? (
                  <>Selected: <span className="font-semibold text-foreground">{format(dateValue, "EEEE, MMMM d, yyyy")}</span></>
                ) : (
                  "Tap a date to select"
                )}
              </p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop: Use Popover
  const { Popover, PopoverContent, PopoverTrigger } = require("@/components/ui/popover");
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {TriggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          defaultMonth={dateValue}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
